import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, Clock } from 'lucide-react';
import { formatDistanceToNow, formatDuration, intervalToDuration } from 'date-fns';

interface TimelineEntry {
  group_id: string;
  group_name: string;
  bot_id: string;
  bot_name: string;
  priority: number;
  active_since: string;
  active_until: string | null;
  duration_minutes: number;
  is_current: boolean;
}

export const GroupTimeline: React.FC = () => {
  const { data: timeline, isLoading } = useQuery({
    queryKey: ['group-timeline'],
    queryFn: async () => {
      // @ts-ignore - strategy_groups table
      const { data: groups } = await supabase
        // @ts-ignore
        .from('strategy_groups')
        .select(`
          id,
          name,
          strategy_group_states(
            active_bot_id,
            active_since
          )
        `)
        .eq('is_active', true);

      if (!groups || groups.length === 0) return [];

      const timelinePromises = groups.map(async (group: any) => {
        // Get historical events for this group's bots
        // @ts-ignore - strategy_group_members table
        const { data: members } = await supabase
          // @ts-ignore
          .from('strategy_group_members')
          .select('bot_id, priority, bots(name)')
          .eq('group_id', group.id)
          .eq('is_active', true);

        const botIds = members?.map(m => m.bot_id) || [];

        if (botIds.length === 0) return [];

        // Get forwarded signals with timestamps to construct timeline
        const { data: events } = await supabase
          .from('conditional_skip_events')
          .select('bot_id, created_at, decision, reason, bots(name)')
          .in('bot_id', botIds)
          .eq('decision', 'FORWARDED')
          .order('created_at', { ascending: false })
          .limit(50);

        if (!events || events.length === 0) return [];

        // Construct timeline entries
        const entries: TimelineEntry[] = [];
        let currentBot: string | null = null;
        let currentBotStart: string | null = null;

        // Get current active bot
        const currentState = group.strategy_group_states;
        const activeBot = currentState?.active_bot_id;
        const activeSince = currentState?.active_since;

        // Parse events in reverse (oldest first)
        const reversedEvents = [...events].reverse();

        reversedEvents.forEach((event, index) => {
          if (event.bot_id !== currentBot) {
            // Bot switch detected
            if (currentBot && currentBotStart) {
              const member = members?.find(m => m.bot_id === currentBot);
              entries.push({
                group_id: group.id,
                group_name: group.name,
                bot_id: currentBot,
                bot_name: (member as any)?.bots?.name || 'Unknown',
                priority: member?.priority || 0,
                active_since: currentBotStart,
                active_until: event.created_at,
                duration_minutes: Math.round(
                  (new Date(event.created_at).getTime() - new Date(currentBotStart).getTime()) / 60000
                ),
                is_current: false
              });
            }

            currentBot = event.bot_id;
            currentBotStart = event.created_at;
          }
        });

        // Add final/current entry
        if (activeBot && activeSince) {
          const member = members?.find(m => m.bot_id === activeBot);
          entries.push({
            group_id: group.id,
            group_name: group.name,
            bot_id: activeBot,
            bot_name: (member as any)?.bots?.name || 'Unknown',
            priority: member?.priority || 0,
            active_since: activeSince,
            active_until: null,
            duration_minutes: Math.round(
              (Date.now() - new Date(activeSince).getTime()) / 60000
            ),
            is_current: true
          });
        }

        return entries;
      });

      const allEntries = (await Promise.all(timelinePromises)).flat();
      return allEntries.sort((a, b) => 
        new Date(b.active_since).getTime() - new Date(a.active_since).getTime()
      );
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  const formatDurationString = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!timeline || timeline.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Group Timeline</CardTitle>
          <CardDescription>Bot activity history and switches</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            No activity history available yet
          </div>
        </CardContent>
      </Card>
    );
  }

  // Group by group_id
  const groupedTimeline = timeline.reduce((acc, entry) => {
    if (!acc[entry.group_id]) {
      acc[entry.group_id] = {
        group_name: entry.group_name,
        entries: []
      };
    }
    acc[entry.group_id].entries.push(entry);
    return acc;
  }, {} as Record<string, { group_name: string; entries: TimelineEntry[] }>);

  return (
    <div className="space-y-4">
      {Object.entries(groupedTimeline).map(([groupId, data]) => (
        <Card key={groupId}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              {data.group_name}
            </CardTitle>
            <CardDescription>Bot activity timeline</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.entries.map((entry, index) => (
                <div
                  key={`${entry.bot_id}-${entry.active_since}`}
                  className={`flex items-center gap-4 p-3 rounded-lg border ${
                    entry.is_current 
                      ? 'border-primary bg-primary/5' 
                      : 'border-border bg-muted/30'
                  }`}
                >
                  {/* Timeline indicator */}
                  <div className="flex flex-col items-center">
                    <div className={`w-3 h-3 rounded-full ${
                      entry.is_current ? 'bg-primary animate-pulse' : 'bg-muted-foreground'
                    }`} />
                    {index < data.entries.length - 1 && (
                      <div className="w-0.5 h-8 bg-border mt-1" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{entry.bot_name}</span>
                      <Badge variant="outline" className="text-xs">P{entry.priority}</Badge>
                      {entry.is_current && (
                        <Badge variant="default" className="text-xs">Active</Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Started {formatDistanceToNow(new Date(entry.active_since), { addSuffix: true })}
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="text-right">
                    <div className="text-lg font-bold">
                      {formatDurationString(entry.duration_minutes)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {entry.is_current ? 'Running' : 'Duration'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
